import { Component } from '@theme/component';

/**
 * Store locator: filterable list of store blocks + an optional Google Maps view.
 * Store data is read straight off the server-rendered `.store-locator__card`
 * elements (data-lat/data-lng/data-name/data-country) instead of a duplicated
 * JSON blob, so the list works even before/without the map.
 *
 * @typedef {object} StoreLocatorRefs
 * @property {HTMLElement} list
 * @property {HTMLElement} map
 * @property {HTMLElement[]} [cards]
 * @property {HTMLElement[]} [filterButtons]
 * @property {HTMLTemplateElement[]} [popupTemplates]
 * @property {HTMLElement} [reviews]
 * @property {HTMLElement} [reviewsStoreName]
 * @property {HTMLElement} [reviewsSummary]
 * @property {HTMLElement} [reviewsStatus]
 * @property {HTMLElement} [reviewsList]
 *
 * @extends {Component<StoreLocatorRefs>}
 */
class StoreLocatorComponent extends Component {
  requiredRefs = ['list', 'map'];

  connectedCallback() {
    super.connectedCallback();

    this.activeCountry = this.dataset.activeCountry || 'all';
    this.map = null;
    this.markers = [];
    this.infoWindow = null;
    this.activeIndex = null;
    /** @type {Map<number, Promise<any>>} */
    this.placeCache = new Map();
    this.reviewsRequest = 0;

    this.setCountry({ country: this.activeCountry });

    const apiKey = this.dataset.apiKey;
    if (apiKey) {
      this.loadGoogleMaps(apiKey);
    }
  }

  /**
   * @param {{country: string}} data
   */
  setCountry({ country }) {
    this.activeCountry = country;

    for (const button of this.refs.filterButtons ?? []) {
      button.classList.toggle('is-active', button.dataset.country === country);
    }

    for (const card of this.refs.cards ?? []) {
      const matches = country === 'all' || card.dataset.country === country;
      card.hidden = !matches;

      if (!matches && this.refs.reviews && Number(card.dataset.index) === this.activeIndex) {
        this.refs.reviews.hidden = true;
      }
    }

    this.updateMarkers();
  }

  /**
   * @param {{index: string | number}} data
   */
  focusStore({ index }) {
    const idx = Number(index);
    this.activeIndex = idx;

    for (const card of this.refs.cards ?? []) {
      card.classList.toggle('is-active', Number(card.dataset.index) === idx);
    }

    if (!this.map) return;

    this.loadReviews(idx);

    const marker = this.markers[idx];
    if (!marker) return;

    this.map.panTo(marker.getPosition());
    this.map.setZoom(14);

    // Pass an Element, not the template's DocumentFragment: a fragment empties
    // itself on first insertion, and the InfoWindow re-inserts its content when
    // it's already open — so every popup after the first rendered blank.
    const template = (this.refs.popupTemplates ?? [])[idx];
    const content = template instanceof HTMLTemplateElement ? template.content.firstElementChild : null;
    if (this.infoWindow && content) {
      this.infoWindow.setContent(content.cloneNode(true));
      this.infoWindow.open(this.map, marker);
    }
  }

  /**
   * @returns {{ index: number, lat: number, lng: number, name: string, country: string }[]}
   */
  getStores() {
    return (this.refs.cards ?? [])
      .map((card, index) => ({
        index,
        lat: Number(card.dataset.lat),
        lng: Number(card.dataset.lng),
        name: card.dataset.name ?? '',
        country: card.dataset.country ?? '',
      }))
      .filter((store) => Number.isFinite(store.lat) && Number.isFinite(store.lng));
  }

  /**
   * @param {string} apiKey
   */
  loadGoogleMaps(apiKey) {
    if (!this.getStores().length) return;

    if (window.google?.maps) {
      this.initMap();
      return;
    }

    // section.id can contain characters that aren't valid in a JS identifier
    // (e.g. "template--177508179965__store_locator_main" in admin preview) —
    // Google's loader splices this callback name straight into generated JS,
    // so an unsanitized "--" followed by digits gets misparsed as a decrement
    // operator + numeric literal ("Only one underscore is allowed as numeric
    // separator"). Strip anything unsafe before using it.
    const safeSectionId = String(this.dataset.sectionId).replace(/[^a-zA-Z0-9_]/g, '_');
    const callbackName = `__initStoreLocatorMap_${safeSectionId}`;

    if (window[callbackName]) return;

    window[callbackName] = () => {
      this.initMap();
      delete window[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  initMap() {
    const placeholder = this.refs.map.querySelector('.store-locator__map-placeholder');
    placeholder?.remove();

    this.map = new google.maps.Map(this.refs.map, {
      zoom: 5,
      center: { lat: 42.7, lng: 25.3 },
    });
    this.infoWindow = new google.maps.InfoWindow();

    for (const store of this.getStores()) {
      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map: this.map,
        title: store.name,
      });

      marker.addListener('click', () => this.focusStore({ index: store.index }));
      this.markers[store.index] = marker;
    }

    this.updateMarkers();
    this.loadLiveRatings();
  }

  /**
   * Fills in the Google rating badge for stores without a manually set rating.
   */
  loadLiveRatings() {
    for (const card of this.refs.cards ?? []) {
      const badge = card.querySelector('[data-live-rating]');
      if (!(badge instanceof HTMLAnchorElement)) continue;

      this.getPlace(Number(card.dataset.index), card)
        .then((place) => {
          if (!place?.rating) return;

          const number = badge.querySelector('.store-locator__rating-badge-number');
          if (number) {
            number.textContent = place.rating.toLocaleString(document.documentElement.lang || undefined, {
              minimumFractionDigits: 1,
            });
          }
          if (!badge.href && place.googleMapsURI) {
            badge.href = place.googleMapsURI;
            badge.target = '_blank';
            badge.rel = 'noopener';
          }
          badge.hidden = false;
        })
        .catch((error) => console.warn('[store-locator] Could not load Google rating', error));
    }
  }

  /**
   * Loads live Google reviews for the selected store into the reviews panel.
   * Requires Places API (New) enabled for the same key as the map.
   * @param {number} index
   */
  async loadReviews(index) {
    const { reviews: panel, reviewsStoreName, reviewsSummary, reviewsList } = this.refs;
    const card = (this.refs.cards ?? [])[index];
    if (!panel || !card || !window.google?.maps?.importLibrary) return;

    // Drop responses for a store that is no longer selected.
    const request = ++this.reviewsRequest;

    panel.hidden = false;
    if (reviewsStoreName) reviewsStoreName.textContent = card.dataset.name ? ` — ${card.dataset.name}` : '';
    reviewsSummary?.replaceChildren();
    reviewsList?.replaceChildren();
    this.setReviewsStatus(panel.dataset.textLoading);

    try {
      const place = await this.getPlace(index, card);
      if (request !== this.reviewsRequest) return;

      const maxReviews = Number(panel.dataset.maxReviews) || 5;
      const reviews = (place?.reviews ?? []).slice(0, maxReviews);

      this.renderReviewsSummary(place);
      if (!reviews.length) {
        this.setReviewsStatus(panel.dataset.textEmpty);
        return;
      }

      this.setReviewsStatus('');
      reviewsList?.replaceChildren(...reviews.map((review) => this.renderReview(review)));
      this.updateReviewToggles();
    } catch (error) {
      if (request !== this.reviewsRequest) return;
      console.warn('[store-locator] Could not load Google reviews', error);
      this.setReviewsStatus(panel.dataset.textError);
    }
  }

  /**
   * Resolves the store to a Google Place (by Place ID, or by a text search on
   * name + address near its coordinates) and fetches its reviews. Cached per
   * store so repeated clicks don't trigger new (billed) requests.
   * @param {number} index
   * @param {HTMLElement} card
   */
  getPlace(index, card) {
    let promise = this.placeCache.get(index);
    if (promise) return promise;

    promise = (async () => {
      const { Place } = await google.maps.importLibrary('places');
      const requestedLanguage = document.documentElement.lang || undefined;
      let placeId = card.dataset.placeId;

      if (!placeId) {
        const lat = Number(card.dataset.lat);
        const lng = Number(card.dataset.lng);
        const { places } = await Place.searchByText({
          textQuery: [card.dataset.name, card.dataset.address].filter(Boolean).join(', '),
          fields: ['id'],
          maxResultCount: 1,
          ...(Number.isFinite(lat) && Number.isFinite(lng) ? { locationBias: { lat, lng } } : {}),
        });
        placeId = places?.[0]?.id;
        if (!placeId) return null;
      }

      const place = new Place({ id: placeId, requestedLanguage });
      await place.fetchFields({ fields: ['reviews', 'rating', 'userRatingCount', 'googleMapsURI'] });
      return place;
    })();

    // Allow a retry on the next click if the request failed.
    promise.catch(() => this.placeCache.delete(index));
    this.placeCache.set(index, promise);
    return promise;
  }

  /**
   * @param {string | undefined} text
   */
  setReviewsStatus(text) {
    if (this.refs.reviewsStatus) this.refs.reviewsStatus.textContent = text ?? '';
  }

  /**
   * @param {any} place
   */
  renderReviewsSummary(place) {
    const summary = this.refs.reviewsSummary;
    const panel = this.refs.reviews;
    if (!summary || !panel || !place) return;

    if (place.rating) {
      const rating = document.createElement('span');
      rating.className = 'store-locator__review-meta';

      const stars = document.createElement('span');
      stars.className = 'store-locator__review-stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = this.formatStars(place.rating);

      const value = document.createElement('strong');
      value.textContent = String(place.rating);

      rating.append(stars, value);
      if (place.userRatingCount) {
        rating.append(` (${place.userRatingCount} ${panel.dataset.textCount ?? ''})`);
      }
      summary.append(rating);
    }

    if (place.googleMapsURI) {
      const link = document.createElement('a');
      link.href = place.googleMapsURI;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = panel.dataset.textAll ?? '';
      summary.append(link);
    }
  }

  /**
   * @param {any} review google.maps.places.Review
   * @returns {HTMLLIElement}
   */
  renderReview(review) {
    const item = document.createElement('li');
    item.className = 'store-locator__review';

    // Google requires the author attribution (name + link) to be shown.
    const attribution = review.authorAttribution;
    const author = document.createElement(attribution?.uri ? 'a' : 'span');
    author.className = 'store-locator__review-author';
    if (author instanceof HTMLAnchorElement && attribution?.uri) {
      author.href = attribution.uri;
      author.target = '_blank';
      author.rel = 'noopener';
    }
    if (attribution?.photoURI) {
      const photo = document.createElement('img');
      photo.className = 'store-locator__review-photo';
      photo.src = attribution.photoURI;
      photo.alt = '';
      photo.loading = 'lazy';
      photo.referrerPolicy = 'no-referrer';
      author.append(photo);
    }
    author.append(attribution?.displayName ?? '');

    const meta = document.createElement('div');
    meta.className = 'store-locator__review-meta';
    if (review.rating) {
      const stars = document.createElement('span');
      stars.className = 'store-locator__review-stars';
      stars.setAttribute('aria-label', `${review.rating}/5`);
      stars.textContent = this.formatStars(review.rating);
      meta.append(stars);
    }
    if (review.relativePublishTimeDescription) {
      meta.append(review.relativePublishTimeDescription);
    }

    item.append(author, meta);

    if (review.text) {
      const text = document.createElement('p');
      text.className = 'store-locator__review-text';
      text.textContent = review.text;

      const panel = this.refs.reviews;
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'store-locator__review-toggle';
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = panel?.dataset.textMore ?? '';
      toggle.addEventListener('click', () => {
        const expanded = item.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = (expanded ? panel?.dataset.textLess : panel?.dataset.textMore) ?? '';
      });

      item.append(text, toggle);
    }

    return item;
  }

  /**
   * Shows "Read more" only on reviews whose text is actually clamped.
   * Must run after the reviews are in the DOM so heights can be measured.
   */
  updateReviewToggles() {
    requestAnimationFrame(() => {
      for (const item of this.refs.reviewsList?.querySelectorAll('.store-locator__review') ?? []) {
        const text = item.querySelector('.store-locator__review-text');
        const toggle = item.querySelector('.store-locator__review-toggle');
        if (!(text instanceof HTMLElement) || !(toggle instanceof HTMLElement)) continue;
        toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
      }
    });
  }

  /**
   * @param {number} rating
   */
  formatStars(rating) {
    const full = Math.max(0, Math.min(5, Math.round(rating)));
    return '★'.repeat(full) + '☆'.repeat(5 - full);
  }

  updateMarkers() {
    if (!this.map) return;

    const stores = this.getStores();
    const visible = stores.filter((store) => this.activeCountry === 'all' || store.country === this.activeCountry);
    const visibleIndexes = new Set(visible.map((store) => store.index));

    this.markers.forEach((marker, index) => {
      marker.setMap(visibleIndexes.has(index) ? this.map : null);
    });

    if (!visible.length) return;

    // fitBounds on a single point zooms all the way in, leaving the marker in
    // an empty map — select the only store instead, as if it was clicked.
    if (visible.length === 1) {
      this.focusStore({ index: visible[0].index });
      return;
    }

    this.infoWindow?.close();

    const bounds = new google.maps.LatLngBounds();
    for (const store of visible) {
      bounds.extend({ lat: store.lat, lng: store.lng });
    }
    this.map.fitBounds(bounds);
  }
}

if (!customElements.get('store-locator-component')) {
  customElements.define('store-locator-component', StoreLocatorComponent);
}
